import React, { useState, useRef, useEffect } from "react";
import "../styles/intake.css";

export default function UploadDocumentsForm({ formData, setFormData }) {
  const [isEditing, setIsEditing] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const clinicId = formData.clinic_id || "default_clinic_id";

  // Load existing files on mount or refresh
  useEffect(() => {
    const existingDocs = Array.isArray(formData.documents) ? formData.documents : [];
    setUploadedFiles(existingDocs);
  }, [formData.documents]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploading(true);

    try {
      const newUploads = [];

      for (const file of files) {
        // Only allow PDFs (your input already filters but this is extra safety)
        const isPdf =
          file.type === "application/pdf" ||
          (file.name || "").toLowerCase().endsWith(".pdf");

        if (!isPdf) {
          alert(`❌ Only PDF files are supported. Skipped: ${file.name}`);
          continue;
        }

        const formDataToSend = new FormData();
        formDataToSend.append("clinic_id", clinicId);
        formDataToSend.append("file", file);

        // Optional: you can add category later if your UI supports it:
        // formDataToSend.append("category", "policies");

        // Optional knobs (omit if you don't need):
        formDataToSend.append("replace_chunks", "true");
        formDataToSend.append("chunk_size", "900");
        formDataToSend.append("chunk_overlap", "120");

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/documents/upload-and-ingest`,
          {
            method: "POST",
            body: formDataToSend,
          }
        );

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          alert("❌ Upload failed: " + (result.detail || "Unknown error"));
          continue;
        }

        // Backend returns: { message, document, legacy_documents, url }
        const doc = result.document;
        const url = result.url;

        newUploads.push({
          // Legacy UI fields (what your UI expects)
          name: file.name,
          type: file.type || "application/pdf",
          size: file.size,
          url,

          // NEW fields (very helpful for later)
          document_id: doc?.id,
          source_id: doc?.source_id,
          storage_bucket: doc?.storage_bucket,
          storage_path: doc?.storage_path,
          status: doc?.status, // likely "uploaded" then later "indexed"
          page_count: doc?.page_count ?? null,
          created_at: doc?.created_at,
        });
      }

      const updatedList = [...uploadedFiles, ...newUploads];
      setUploadedFiles(updatedList);

      // keep clinic profile draft updated
      setFormData({
        ...formData,
        documents: updatedList,
      });
    } catch (error) {
      alert("❌ Upload error: " + error.message);
    } finally {
      setIsUploading(false);
      // reset input so selecting same file again triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Remove logic (kept as-is, but note below)
  const handleRemove = async (indexToRemove) => {
    const file = uploadedFiles[indexToRemove];
  
    try {
      let res;
  
      // ✅ New path: delete by document_id (preferred)
      if (file.document_id) {
        res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/documents/${file.document_id}/delete`,
          { method: "POST" }
        );
      } else {
        // 🔁 Fallback legacy path: delete by URL
        res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/delete-document`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clinic_id: formData.clinic_id,
            file_url: file.url,
          }),
        });
      }
  
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error("Failed to delete:", err.detail || "Unknown error");
        alert("Failed to delete file.");
        return;
      }
  
      // Remove from state
      const updated = [...uploadedFiles];
      updated.splice(indexToRemove, 1);
      setUploadedFiles(updated);
  
      // Update formData.documents
      const updatedDocuments = [...(formData.documents || [])];
      updatedDocuments.splice(indexToRemove, 1);
      setFormData((prev) => ({ ...prev, documents: updatedDocuments }));
    } catch (error) {
      console.error("Delete error:", error);
      alert("Something went wrong.");
    }
  };
  

  const handleSave = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/save-clinic-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clinic_id: formData.clinic_id,
          documents: uploadedFiles, // includes legacy + extra fields
        }),
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        alert("❌ Failed to save documents: " + (error.detail || "Unknown error"));
        return;
      }

      alert("✅ Documents saved successfully.");
      setIsEditing(false);
    } catch (err) {
      alert("❌ Error saving documents: " + err.message);
    }
  };

  const handleEdit = () => setIsEditing(true);
  const triggerFileUpload = () => fileInputRef.current.click();

  return (
    <div className="form-wrapper">
      <div className="form-section-header">
        <h2>
          8. <span className="section-title">Custom PDFs or Documents</span>
        </h2>
        <div className="form-buttons">
          {isEditing ? (
            <button className="blue-button" onClick={handleSave} disabled={isUploading}>
              Save
            </button>
          ) : (
            <button className="blue-button" onClick={handleEdit}>
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="separator" />

      <div className="upload-note">
        <p
          style={{
            marginBottom: "30px",
            color: "#004aad",
            fontSize: "16px",
            marginTop: "-10px",
            width: "100%",
            textAlign: "left",
          }}
        >
          <strong>📎 Please upload any documents your clinic uses to inform or guide your clients.</strong>{" "}
          This will help us train your AI assistant to answer questions accurately and reflect your brand’s tone and procedures.
          <br />
          <br />
          Recommended documents include:
          <ul>
            <li>🧾 Price Lists and Treatment Menus</li>
            <li>📋 Pre-Appointment Intake Forms (e.g., medical history, skin concerns)</li>
            <li>✅ Consent Forms (e.g., Botox, laser, microneedling)</li>
            <li>📄 Pre- and Post-Treatment Instructions</li>
            <li>🧑‍⚕️ Treatment Descriptions or Protocols</li>
            <li>🧴 Product Recommendations or Skincare Instructions</li>
            <li>🧭 Clinic Welcome Packets or New Client Guides</li>
            <li>📅 Cancellation, Late, or Rescheduling Policy Documents</li>
            <li>🛡️ Liability Waivers or Disclaimers</li>
            <li>💬 Frequently Asked Questions (FAQs)</li>
            <li>🗣️ Message Templates (e.g., SMS/email follow-ups)</li>
            <li>🗂️ Membership Plans, Loyalty Programs, Promotions</li>
            <li>🏷️ Gift Card Terms, Referral Policies</li>
            <li>🌐 Optional: Staff Bios, Branding Guides, Ideal Client Profiles</li>
          </ul>
          The more details you provide, the smarter your AI assistant will be!
        </p>
      </div>

      {isEditing && (
        <>
          <input
            type="file"
            accept="application/pdf"
            multiple
            onChange={handleUpload}
            ref={fileInputRef}
            style={{ display: "none" }}
            disabled={isUploading}
          />
          <button className="add-button" onClick={triggerFileUpload} disabled={isUploading}>
            {isUploading ? "Uploading..." : "+ Upload Documents"}
          </button>
        </>
      )}

      {uploadedFiles.length > 0 && (
        <div className="uploaded-file-list">
          {uploadedFiles.map((file, index) => (
            <div key={index} className="uploaded-file-item">
              <div>
                <a href={file.url} target="_blank" rel="noopener noreferrer">
                  <strong>{file.name}</strong>
                </a>
                <br />
                <span>Type: {file.type}</span>
                <br />
                <span>Size: {(file.size / 1024).toFixed(1)} KB</span>

                {/* Optional: display ingest status */}
                {file.status && (
                  <>
                    <br />
                    <span>Status: {file.status}</span>
                  </>
                )}
              </div>

              {isEditing && (
                <button className="remove-button small" onClick={() => handleRemove(index)} disabled={isUploading}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

